// Navigation service for handling feature navigation and return behavior
import { useEffect, useRef } from 'react';

export class NavigationState {
    constructor() {
        this.positions = new Map();
        this.currentFeature = null;
        this.sourceFeature = null;
        this.highlightedFeature = null;
    }
}

const HIGHLIGHT_DURATION = 3000; // Duration in ms to show highlight

export const useFeatureNavigation = (onNavigationComplete) => {
    const navigationState = useRef(new NavigationState());
    const highlightTimeout = useRef(null);

    const savePosition = (featureRef) => {
        if (featureRef) {
            navigationState.current.positions.set(featureRef, window.scrollY);
        }
    };

    const navigateToFeature = async (targetFeatureRef, sourceFeatureRef = null) => {
        // Save current position
        savePosition(navigationState.current.currentFeature);

        // Update navigation state
        navigationState.current.sourceFeature = sourceFeatureRef;
        navigationState.current.currentFeature = targetFeatureRef;

        try {
            // Find the target element
            const targetElement = document.querySelector(`[data-feature-ref="${targetFeatureRef}"]`);
            if (!targetElement) {
                console.warn(`Feature element ${targetFeatureRef} not found`);
                return;
            }

            // Calculate scroll position
            const elementRect = targetElement.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const middle = absoluteElementTop - (window.innerHeight / 3);

            // Smooth scroll to the element
            await window.scrollTo({
                top: middle,
                behavior: 'smooth'
            });

            // Highlight the target feature
            navigationState.current.highlightedFeature = targetFeatureRef;
            
            // Clear any existing highlight timeout
            if (highlightTimeout.current) {
                clearTimeout(highlightTimeout.current);
            }

            // Set new highlight timeout
            highlightTimeout.current = setTimeout(() => {
                navigationState.current.highlightedFeature = null;
                if (onNavigationComplete) {
                    onNavigationComplete();
                }
            }, HIGHLIGHT_DURATION);

        } catch (error) {
            console.error('Navigation failed:', error);
        }
    };

    const returnToSource = async () => {
        const sourceFeature = navigationState.current.sourceFeature;
        if (!sourceFeature) return;

        const savedPosition = navigationState.current.positions.get(sourceFeature);
        if (savedPosition !== undefined) {
            await window.scrollTo({
                top: savedPosition,
                behavior: 'smooth'
            });
        }

        // Clear navigation state
        navigationState.current.currentFeature = sourceFeature;
        navigationState.current.sourceFeature = null;
        navigationState.current.highlightedFeature = null;

        // Clear positions except for source
        navigationState.current.positions.clear();

        if (onNavigationComplete) {
            onNavigationComplete();
        }
    };

    const isFeatureHighlighted = (featureRef) => {
        return navigationState.current.highlightedFeature === featureRef;
    };

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (highlightTimeout.current) {
                clearTimeout(highlightTimeout.current);
            }
        };
    }, []);

    return {
        navigateToFeature,
        returnToSource,
        isFeatureHighlighted,
        getCurrentFeature: () => navigationState.current.currentFeature,
        getSourceFeature: () => navigationState.current.sourceFeature
    };
};